/**
 * Frontend Sentry Initialization
 * Only initializes if SENTRY_DSN is available at build time
 */

import * as Sentry from '@sentry/react';

export function initFrontendSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn) {
    console.log('ℹ️  Sentry DSN not set - skipping frontend Sentry');
    return;
  }

  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 
                      import.meta.env.MODE || 
                      'production';
  
  const release = import.meta.env.VITE_SENTRY_RELEASE ||
                  import.meta.env.CIRCLE_SHA1 ||
                  import.meta.env.GITHUB_SHA ||
                  undefined;

  Sentry.init({
    dsn,
    environment,
    release,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1, // 10% of transactions
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    beforeSend(event, hint) {
      // Scrub sensitive data
      return scrubSensitiveData(event);
    },
  });

  console.log(`✅ Frontend Sentry initialized (env: ${environment}, release: ${release || 'none'})`);
}

/**
 * Scrub sensitive data from Sentry events
 */
function scrubSensitiveData(event: Sentry.Event): Sentry.Event | null {
  if (!event) return event;

  // Scrub request headers
  if (event.request?.headers) {
    const headers = { ...event.request.headers };
    const secretPatterns = ['token', 'key', 'secret', 'password', 'auth', 'authorization'];
    
    for (const [key, value] of Object.entries(headers)) {
      const lowerKey = key.toLowerCase();
      if (secretPatterns.some(pattern => lowerKey.includes(pattern))) {
        headers[key] = '[REDACTED]';
      }
    }
    event.request.headers = headers;
  }

  // Scrub URL query params
  if (event.request?.url) {
    try {
      const url = new URL(event.request.url);
      const secretParams = ['token', 'key', 'secret', 'password', 'auth'];
      secretParams.forEach(param => {
        if (url.searchParams.has(param)) {
          url.searchParams.set(param, '[REDACTED]');
        }
      });
      event.request.url = url.toString();
    } catch {
      // Ignore URL parsing errors
    }
  }

  return event;
}

